/**
 * Session-scoped watermark for the post-stream reveal: how much of a message's
 * rendered text has already been shown, in characters. A character offset
 * rather than a unit count, because streaming markdown changes shape (a line
 * becomes a list, a table, a code block) and shifts unit indices, while the
 * shown text stays put. FinalMessageReveal seeds its local
 * watermark from here and writes progress back, so a remount within a session
 * (chat switch, stream resume, refetch swap) continues or skips the reveal
 * instead of replaying it — each unit animates at most once per renderer
 * session. Monotone by construction; unknown ids (fresh session, unpersisted
 * placeholder) read as zero.
 */
const revealedTextLengths = new Map<string, number>()

/**
 * Conversations that had a live turn render in this session. Lets the reveal
 * fire even when the message row remounted between streaming and turn end
 * (its mount-local "saw streaming" ref resets) — the watermark above is what
 * keeps that from ever replaying an already-shown reveal.
 */
const streamedConversationIds = new Set<string>()

export function markConversationStreamed(conversationId: string | undefined) {
  if (conversationId !== undefined) streamedConversationIds.add(conversationId)
}

export function didConversationStream(conversationId: string | undefined): boolean {
  return conversationId !== undefined && streamedConversationIds.has(conversationId)
}

export function getRevealedLength(messageId: string | undefined): number {
  return messageId !== undefined ? (revealedTextLengths.get(messageId) ?? 0) : 0
}

export function advanceRevealedLength(messageId: string | undefined, length: number) {
  if (messageId === undefined) return
  revealedTextLengths.set(messageId, Math.max(getRevealedLength(messageId), length))
}
